import React, { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Paper,
  IconButton,
  TextField,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material'
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  NavigateBefore as PreviousIcon,
  NavigateNext as NextIcon
} from '@mui/icons-material'
import { salesService } from '../../services/api'
import { useAuth } from '../auth/AuthProvider'

interface FloatingNavigationBarProps {
  currentBondNum?: string
  onNavigate: (bondNum: string) => void
  branchId: number
}

interface NavigationState {
  currentPage: number
  totalPages: number
  isLoading: boolean
  error: string | null
}

const FloatingNavigationBar: React.FC<FloatingNavigationBarProps> = ({
  currentBondNum,
  onNavigate,
  branchId
}) => {
  const { isAuthenticated } = useAuth()
  
  const [searchInput, setSearchInput] = useState('')
  const [navState, setNavState] = useState<NavigationState>({
    currentPage: 1,
    totalPages: 1,
    isLoading: false,
    error: null
  })
  const [showError, setShowError] = useState(false)
  const [hasNavigated, setHasNavigated] = useState(false)
  
  // Debug: Check if component is mounting
  useEffect(() => {
    console.log('🏗️ FloatingNavigationBar component mounted/initialized')
  }, [])

  // Update search input when currentBondNum changes
  useEffect(() => {
    console.log('🔄 currentBondNum changed:', currentBondNum, 'current navState:', navState)
    console.log('🔄 hasNavigated:', hasNavigated, 'totalPages:', navState.totalPages)
    if (currentBondNum && currentBondNum !== searchInput) {
      console.log('📝 Updating search input from', searchInput, 'to', currentBondNum)
      setSearchInput(currentBondNum)
    }
  }, [currentBondNum]) // eslint-disable-line react-hooks/exhaustive-deps 


  // Load navigation state and reset when branchId changes
  useEffect(() => {
    console.log('🔁 Loading navigation state for branchId:', branchId)
    // Reset state on branch change
    setNavState(prev => ({ ...prev, currentPage: 1, totalPages: 1, isLoading: false, error: null }))
    setHasNavigated(false)

    const loadInitialState = async () => {
      if (!isAuthenticated) return
      try {
        setNavState(prev => ({ ...prev, isLoading: true, error: null }))

        const response = await salesService.list({
          def_branch: branchId,
          ordering: 'asc',
          page: 1
        })

        // Pagination metadata is at top-level in Laravel pagination
        const pagination = response as any
        const lastPage = pagination?.last_page ?? 1
        const currentPage = pagination?.current_page ?? 1

        setNavState(prev => ({
          ...prev,
          totalPages: lastPage || 1,
          currentPage: currentPage || 1,
          isLoading: false
        }))
      } catch (error) {
        console.error('Failed to load navigation state:', error)
        setNavState(prev => ({
          ...prev,
          isLoading: false,
          error: 'فشل في تحميل بيانات التنقل'
        }))
      }
    }

    loadInitialState()
  }, [branchId, isAuthenticated])

  // Navigate to specific page
  const navigateToPage = useCallback(async (page: number) => {
    console.log('🔄 navigateToPage called with:', page)
    
    if (!isAuthenticated || navState.isLoading || page < 1 || page > navState.totalPages) {
      console.log('❌ Navigation blocked:', { 
        isAuthenticated, 
        isLoading: navState.isLoading, 
        page, 
        totalPages: navState.totalPages 
      })
      return
    }

    try {
      setNavState(prev => ({ ...prev, isLoading: true, error: null }))
      
      const response = await salesService.list({
        def_branch: branchId,
        ordering: 'asc',
        page: page
      })
      
      console.log('🔍 Navigation response:', response)
      console.log('🔍 Navigation response.data:', response?.data)
      
      // Check if response.data is an array (invoice data)
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        const bondNum = response.data[0].bond_num
        console.log('✅ Found invoice with bondNum:', bondNum)
        console.log('🔧 About to update navigation state to currentPage:', page)
        const paginationMeta = response as any
        const lastPage = paginationMeta?.last_page
        setNavState(prev => {
          console.log('🔧 setNavState called - prev:', prev, 'new currentPage:', page)
          return {
            ...prev,
            currentPage: page,
            totalPages: lastPage || prev.totalPages,
            isLoading: false
          }
        })
        setHasNavigated(true) // Mark that we've navigated
        console.log('🔧 setHasNavigated(true) called')
        onNavigate(bondNum)
        setSearchInput(bondNum)
      } else {
        console.log('❌ No invoice data found in response.data')
        throw new Error('No data found for this page')
      }
    } catch (error) {
      console.error('Navigation error:', error)
      setNavState(prev => ({
        ...prev,
        isLoading: false,
        error: 'فشل في التنقل إلى الصفحة المطلوبة'
      }))
      setShowError(true)
    }
  }, [isAuthenticated, navState.isLoading, navState.totalPages, branchId, onNavigate])

  // Navigate to first page
  const goToFirst = useCallback(() => {
    navigateToPage(1)
  }, [navigateToPage])

  // Navigate to last page
  const goToLast = useCallback(() => {
    navigateToPage(navState.totalPages)
  }, [navigateToPage, navState.totalPages])

  // Navigate to previous page
  const goToPrevious = useCallback(() => {
    if (navState.currentPage > 1) {
      navigateToPage(navState.currentPage - 1)
    }
  }, [navigateToPage, navState.currentPage])

  // Navigate to next page
  const goToNext = useCallback(() => {
    if (navState.currentPage < navState.totalPages) {
      navigateToPage(navState.currentPage + 1)
    }
  }, [navigateToPage, navState.currentPage, navState.totalPages])

  // Helper function to format bond number like backend CompleteBondNum service
  const formatBondNumber = (inputValue: string, branchId: number): string => {
    // Get branch code - for now using branchId, but could be enhanced to use actual branch.code
    const branchCode = branchId.toString().padStart(3, '0')
    
    // Pad the input value to 7 digits  
    const sequentialNumber = inputValue.padStart(7, '0')
    
    return branchCode + sequentialNumber
  }

  // Search for specific bond number using the list endpoint
  const searchBondNum = useCallback(async () => {
    if (!searchInput.trim() || navState.isLoading) return

    try {
      setNavState(prev => ({ ...prev, isLoading: true, error: null }))
      
      const inputValue = searchInput.trim()
      let bondNumToSearch = inputValue
      
      // If input is just numbers (like "2"), format it with branch prefix
      if (/^\d+$/.test(inputValue)) {
        bondNumToSearch = formatBondNumber(inputValue, branchId)
        console.log('🔍 Formatted bond number:', inputValue, '→', bondNumToSearch)
      }
      
      // Use list endpoint with bond_num filter instead of non-existent show
      const response = await salesService.list({
        def_branch: branchId,
        bond_num: bondNumToSearch,
        ordering: 'asc'
      })
      
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        // Extract the sequential part (last 7 digits) from bond number to determine page
        // Bond format: BBB0000000 where BBB = branch code, 0000000 = sequential number
        const sequentialPart = bondNumToSearch.slice(-7) // Get last 7 digits
        const currentInvoicePage = parseInt(sequentialPart, 10) // Convert to number = page number
        
        console.log('🔍 Search Debug Info:')
        console.log('  - bondNumToSearch:', bondNumToSearch)
        console.log('  - sequentialPart:', sequentialPart)
        console.log('  - currentInvoicePage:', currentInvoicePage)
        
        setNavState(prev => ({ 
          ...prev, 
          isLoading: false,
          currentPage: currentInvoicePage
        }))
        setHasNavigated(true) // Mark that we've navigated
        
        console.log('  - Updated currentPage to:', currentInvoicePage)
        onNavigate(bondNumToSearch)
        setSearchInput(bondNumToSearch) // Update search input to show full bond number
      } else {
        throw new Error('Invoice not found')
      }
    } catch (error) {
      console.error('Search error:', error)
      setNavState(prev => ({
        ...prev,
        isLoading: false,
        error: `لم يتم العثور على فاتورة برقم ${searchInput}`
      }))
      setShowError(true)
    }
  }, [searchInput, navState.isLoading, onNavigate, branchId])

  // Handle search input key press
  const handleSearchKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      searchBondNum()
    }
  }, [searchBondNum])

  if (!isAuthenticated) {
    return null
  }

  return (
    <>
      <Paper 
        elevation={6}
        sx={{ 
          position: 'fixed',
          bottom: { xs: 'calc(env(safe-area-inset-bottom) + 16px)', sm: 32 },
          left: '50%',
          right: 'auto',
          transform: 'translateX(-50%)',
          zIndex: 1300,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: { xs: 3, sm: 5 },
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          minWidth: { xs: 'auto', sm: 320 },
          maxWidth: { xs: 'calc(100% - 16px)', sm: 520 },
          p: { xs: 1, sm: 1.5 },
          display: 'flex',
          alignItems: 'center',
          minHeight: 56,
          boxShadow: { xs: '0 6px 20px rgba(0,0,0,0.18)', sm: undefined },
        }}
      >
        <Box display="flex" alignItems="center" sx={{ gap: { xs: 0.5, sm: 1 }, width: '100%', height: '100%', flexWrap: 'nowrap', overflowX: 'auto', whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
          {/* First Page Button */}
          <Tooltip title="أول صفحة">
            <span>
              <IconButton
                onClick={goToFirst}
                disabled={navState.currentPage <= 1 || navState.isLoading}
                size="small"
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  p: 0,
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:disabled': { 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.4)'
                  }
                }}
              >
                <FirstPageIcon sx={{ transform: 'scaleX(-1)', fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Previous Page Button */}
          <Tooltip title="الصفحة السابقة">
            <span>
              <IconButton
                onClick={() => {
                  console.log('🔍 Previous button clicked - navState:', navState)
                  goToPrevious()
                }}
                disabled={navState.currentPage <= 1 || navState.isLoading}
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  p: 0,
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:disabled': { 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.4)'
                  }
                }}
              >
                <PreviousIcon sx={{ transform: 'scaleX(-1)', fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Search Input - Centered */}
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            onFocus={() => setSearchInput('')}
            placeholder="رقم السند"
            size="small"
            sx={{
              flex: 1,
              minWidth: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 3,
              mx: { xs: 0.5, sm: 1 },
              '& .MuiOutlinedInput-root': {
                height: 40,
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: '2px solid rgba(255, 255, 255, 0.8)' }
              },
              '& .MuiInputBase-input': {
                padding: '0 12px',
                height: 40,
                lineHeight: '40px',
                fontSize: '0.95rem',
                textAlign: 'center',
                color: '#333'
              }
            }}
          />

          {/* Next Page Button */}
          <Tooltip title="الصفحة التالية">
            <span>
              <IconButton
                onClick={goToNext}
                disabled={navState.currentPage >= navState.totalPages || navState.isLoading}
                size="small"
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:disabled': { 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.4)'
                  }
                }}
              >
                <NextIcon sx={{ transform: 'scaleX(-1)', fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Last Page Button */}
          <Tooltip title="آخر صفحة">
            <span>
              <IconButton
                onClick={goToLast}
                disabled={navState.currentPage >= navState.totalPages || navState.isLoading}
                size="small"
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:disabled': { 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.4)'
                  }
                }}
              >
                <LastPageIcon sx={{ transform: 'scaleX(-1)', fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={4000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setShowError(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {navState.error}
        </Alert>
      </Snackbar>
    </>
  )
}

export default FloatingNavigationBar
